import { Divider, TextField } from '@mui/material';
import CurrencySelector from 'components/CurrencySelector';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { FC, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  antiDilutionRightsOptions,
  couponRightsOptions,
  liquidationPreferenceTypeOptions,
  monitoringRightsOptions,
} from './consts';

import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import TypographyButton from 'further-ui/components/TypographyButton';

interface HoldingClassRightsFormProps {
  index: number;
  formName: string;
  control: Control;
  vehicleCurrencyCode: CurrencyCode;
  disableCurrency?: boolean;
  assetBaseCurrency?: CurrencyCode;
  expandable?: boolean;
}

const HoldingClassRightsForm: FC<HoldingClassRightsFormProps> = ({
  index,
  formName,
  control,
  vehicleCurrencyCode,
  disableCurrency,
  assetBaseCurrency,
  expandable = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <Column spacing="lg" fullWidth>
      <FormField size="md" label="Class name*">
        <Controller
          name={`${formName}.${index}.name`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              fullWidth
              {...field}
              size="small"
              error={!!error}
              helperText={error?.message}
              placeholder="Enter class name"
            />
          )}
        />
      </FormField>

      <FormField
        size="md"
        label="Currency"
        tooltip={
          disableCurrency
            ? 'Holding class currency is fixed once an investment has been made. To edit, please delete the applicable investment first.'
            : ''
        }
      >
        <Controller
          name={`${formName}.${index}.currency`}
          control={control}
          defaultValue={assetBaseCurrency ?? vehicleCurrencyCode}
          render={({ field: { ref, ...rest }, fieldState: { error } }) => (
            <CurrencySelector
              {...rest}
              error={error ? { message: error.message } : undefined}
              placeholder="Select currency"
              disabled={disableCurrency ?? false}
            />
          )}
        />
      </FormField>

      {expandable ? (
        <>
          <Divider />
          <TypographyButton
            textDecoration="none"
            text="Add additional holding class data"
            showExpandableStartAdornment
            isExpanded={isExpanded}
            onClick={() => setIsExpanded((prev) => !prev)}
          />
        </>
      ) : null}
      {(!expandable || isExpanded) && (
        <Column spacing="lg" fullWidth>
          <FormField size="md" label="Monitoring rights">
            <Controller
              name={`${formName}.${index}.monitoringRights`}
              control={control}
              render={({
                field: { ref, onChange, value, ...rest },
                fieldState: { error },
              }) => (
                <BasicSelect
                  {...rest}
                  value={value || ''}
                  onChange={(value) => onChange(value ?? undefined)}
                  fullWidth
                  options={monitoringRightsOptions.map((option) => ({
                    value: option.id,
                    label: option.title,
                  }))}
                  error={!!error}
                  helperText={error?.message}
                  placeholder="Select monitoring rights"
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Liquidation preference type">
            <Controller
              name={`${formName}.${index}.liquidationPreferenceType`}
              control={control}
              render={({
                field: { ref, onChange, value, ...rest },
                fieldState: { error },
              }) => (
                <BasicSelect
                  {...rest}
                  value={value || ''}
                  onChange={(value) => onChange(value ?? undefined)}
                  fullWidth
                  options={liquidationPreferenceTypeOptions.map((option) => ({
                    value: option.id,
                    label: option.title,
                  }))}
                  error={!!error}
                  placeholder="Select liquidation preference type"
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Liquidation preference multiple">
            <Controller
              name={`${formName}.${index}.liquidationPreferenceMultiple`}
              control={control}
              render={({
                field: { ref, onChange, value, ...rest },
                fieldState: { error },
              }) => (
                <NumberInput
                  {...rest}
                  fullWidth
                  decimals={2}
                  value={value ?? ''}
                  onChange={(value) => {
                    onChange(value ? Number(value) : 0);
                  }}
                  error={!!error}
                  helperText={error?.message}
                  placeholder="Enter liquidation preference multiple"
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Anti-dilution rights">
            <Controller
              name={`${formName}.${index}.antiDilutionRights`}
              control={control}
              render={({
                field: { ref, onChange, value, ...rest },
                fieldState: { error },
              }) => (
                <BasicSelect
                  {...rest}
                  value={value ?? ''}
                  onChange={(value) => onChange(value ?? undefined)}
                  fullWidth
                  options={antiDilutionRightsOptions.map((option) => ({
                    value: option.id,
                    label: option.title,
                  }))}
                  error={!!error}
                  placeholder="Select anti-dilution rights"
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Coupon rights">
            <Controller
              name={`${formName}.${index}.couponRights`}
              control={control}
              render={({
                field: { ref, onChange, value, ...rest },
                fieldState: { error },
              }) => (
                <BasicSelect
                  {...rest}
                  value={value ?? ''}
                  onChange={(value) => onChange(value ?? undefined)}
                  fullWidth
                  options={couponRightsOptions.map((option) => ({
                    value: option.id,
                    label: option.title,
                  }))}
                  error={!!error}
                  placeholder="Select coupon rights"
                  helperText={error?.message}
                />
              )}
            />
          </FormField>
          <FormField size="md" label="Coupon amount (per annum)">
            <Controller
              name={`${formName}.${index}.couponAmount`}
              control={control}
              render={({
                field: { ref, onChange, value, ...rest },
                fieldState: { error },
              }) => (
                <NumberInput
                  {...rest}
                  fullWidth
                  decimals={0}
                  showPercentSuffix
                  value={value ?? null}
                  onChange={(value) => {
                    onChange(value ? Number(value) : null);
                  }}
                  error={!!error}
                  helperText={error?.message}
                  placeholder="Enter coupon amount"
                />
              )}
            />
          </FormField>
        </Column>
      )}
    </Column>
  );
};

export default HoldingClassRightsForm;
